import { useEffect, useRef, useState } from 'react';

/**
 * Fallback camera hook — starts camera only if video element doesn't already have a stream.
 * WebEyeTracker's WebcamClient starts the camera first; this is a safety net.
 *
 * @param delay - ms to wait before checking (gives WebEyeTracker time to start first)
 */
export function useCamera(videoElementId: string, delay: number = 3000) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    let disposed = false;

    const timer = setTimeout(async () => {
      try {
        const videoEl = document.getElementById(videoElementId) as HTMLVideoElement;
        if (!videoEl || disposed) return;

        // If WebEyeTracker already started the camera, skip
        if (videoEl.srcObject) {
          console.log('[useCamera] Video already has stream (WebEyeTracker active), skipping');
          setIsReady(true);
          return;
        }

        console.log('[useCamera] No stream detected, starting fallback camera...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: false,
        });

        if (disposed) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        // Double-check — WebEyeTracker might have started in the meantime
        if (videoEl.srcObject) {
          stream.getTracks().forEach(t => t.stop());
          setIsReady(true);
          return;
        }

        streamRef.current = stream;
        videoEl.srcObject = stream;
        videoEl.play().catch(() => {});
        setIsReady(true);
        console.log('[useCamera] Fallback camera started');
      } catch (err: any) {
        if (!disposed) {
          setError(err.message || '카메라 접근 실패');
          console.warn('[useCamera] Failed:', err);
        }
      }
    }, delay);

    return () => {
      disposed = true;
      clearTimeout(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [videoElementId, delay]);

  return { isReady, error };
}
